/**
 * R-3 — per-tenant citation density floor from Firestore admin config.
 *
 * Doc path: customers/{customerId}/config/citationDensityThreshold
 * Fields: { threshold: number } — clamped to [0.50, 1.0], default 0.80.
 */
import { AUDIT_CONFIG } from '../config/auditConfig';
import { DEFAULT_DENSITY_THRESHOLD } from '../lib/audit/citationDensityGate';

export interface CitationDensityThresholdConfig {
  threshold: number;
  effectiveThreshold: number;
  hasOverride: boolean;
}

function clampTenantThreshold(raw: unknown): number {
  const min = AUDIT_CONFIG.CITATION_DENSITY_MIN_THRESHOLD;
  const fallback = AUDIT_CONFIG.CITATION_DENSITY_DEFAULT_THRESHOLD;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN;
  if (!Number.isFinite(n)) return fallback;
  if (n < min) return min;
  if (n > 1) return 1;
  return n;
}

export function getDefaultCitationDensityThreshold(): number {
  return AUDIT_CONFIG.CITATION_DENSITY_DEFAULT_THRESHOLD;
}

/**
 * Resolve effective threshold for a tenant. `customerId` is typically the
 * site/company tenant id (`site.companyId`).
 */
export async function loadCitationDensityThreshold(
  customerId?: string | null,
): Promise<CitationDensityThresholdConfig> {
  const fallback = getDefaultCitationDensityThreshold();
  if (!customerId) {
    return { threshold: fallback, effectiveThreshold: fallback, hasOverride: false };
  }
  try {
    // BUG-3 FIX — in a headless Cloud Run job there is no signed-in user, so a
    // client-web-SDK read is rejected by Firestore rules (7 PERMISSION_DENIED).
    // Read via the admin SDK (ADC, bypasses rules) when headless, mirroring the
    // report save in the same job. Browser keeps the client path unchanged.
    const { isHeadlessContext, getAdminFirestoreForContext } = await import(
      './firestoreAdminForContext'
    );
    let data: { threshold?: unknown } | undefined;
    let exists = false;
    const adminDb = isHeadlessContext() ? await getAdminFirestoreForContext() : null;
    if (adminDb) {
      const snap = await adminDb
        .doc(`customers/${customerId}/config/citationDensityThreshold`)
        .get();
      exists = snap.exists;
      data = exists ? (snap.data() as { threshold?: unknown }) : undefined;
    } else {
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const ref = doc(db, 'customers', customerId, 'config', 'citationDensityThreshold');
      const snap = await getDoc(ref);
      exists = snap.exists();
      data = exists ? (snap.data() as { threshold?: unknown }) : undefined;
    }
    if (!exists || !data) {
      return { threshold: fallback, effectiveThreshold: fallback, hasOverride: false };
    }
    const effectiveThreshold = clampTenantThreshold(data.threshold ?? fallback);
    return {
      threshold: typeof data.threshold === 'number' ? data.threshold : fallback,
      effectiveThreshold,
      hasOverride: true,
    };
  } catch (e) {
    console.warn('[R-3] Failed to load citation density threshold override, using default:', e);
    return { threshold: fallback, effectiveThreshold: fallback, hasOverride: false };
  }
}

export { DEFAULT_DENSITY_THRESHOLD };
